import type { HanziWord, PinyinlyObjectId } from "@/data/model";
import {
  assetIdFromPinyinlyObjectId,
  assetIdPinyinlyObjectIdKind,
  hanziWordFromPinyinlyObjectId,
  hanziWordPinyinlyObjectId,
  hanziWordPinyinlyObjectIdKind,
  pinyinlyObjectIdKind,
  pinyinSoundIdPinyinlyObjectId,
  pinyinSoundIdPinyinlyObjectIdKind,
  skillIdFromPinyinlyObjectId,
  skillPinyinlyObjectIdKind,
  soundIdFromPinyinlyObjectId,
} from "@/data/model";
import { quickSearchPickSetting } from "@/data/userSettings";
import { hanziFromHanziWord } from "@/dictionary";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useDebounce } from "@uidotdev/usehooks";
import type { Href } from "expo-router";
import { Link } from "expo-router";
import type { ViewProps } from "react-native";
import { ScrollView } from "react-native";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";
import type { QuickSearchResult } from "./hooks/useQuickSearch";
import { useQuickSearch } from "./hooks/useQuickSearch";
import { useDb } from "./hooks/useDb";
import { useRizzle } from "./hooks/useRizzle";
import { useUserSetting } from "./hooks/useUserSetting";
import { useUserSettingHistory } from "./hooks/useUserSettingHistory";
import { RectButton } from "./RectButton";

function quickSearchObjectIdForResult(
  result: QuickSearchResult,
): PinyinlyObjectId | null {
  switch (result.kind) {
    case `hanziWord`:
      return hanziWordPinyinlyObjectId(result.hanziWord);

    case `wikiDirect`:
      return null;

    case `pinyinSound`:
      return pinyinSoundIdPinyinlyObjectId(result.pinyinSoundId);

    default:
      result satisfies never;
      throw new Error(
        `Unknown result quick search result: ${JSON.stringify(result)}`,
      );
  }
}

export function QuickSearchResults({
  query,
  onChangeQuery,
  onSelect,
}: {
  query: string;
  onChangeQuery: (q: string) => void;
  onSelect: () => void;
}) {
  const debouncedQuery = useDebounce(query.trim(), 200);
  const results = useQuickSearch(debouncedQuery);

  const r = useRizzle();
  const { setValue: setQuickSearchPick } = useUserSetting({
    setting: quickSearchPickSetting,
  });

  const hasQuery = debouncedQuery.length > 0;

  const onSelectResult = (result: QuickSearchResult) => {
    const objectId = quickSearchObjectIdForResult(result);

    if (objectId != null) {
      setQuickSearchPick({ objectId });
    }

    onSelect();
  };

  const onRemoveRecent = (id: string) => {
    void r.mutate.deleteSettingHistory({ id }).catch((error: unknown) => {
      console.error(`Failed to delete quick search history "${id}":`, error);
    });
  };

  return (
    <ScrollView className="flex-1" keyboardShouldPersistTaps="handled">
      {hasQuery === false ? (
        <RecentQueries
          onRemove={onRemoveRecent}
          onSelect={() => {
            // Don't re-add recent items to the recent history when selecting
            // them, otherwise we end up with duplicates.
            onSelect();
          }}
        />
      ) : results.length === 0 ? (
        <NoResults query={debouncedQuery} onChangeQuery={onChangeQuery} />
      ) : (
        results.map((result) => (
          <SearchResultItem
            key={
              result.kind === `wikiDirect`
                ? `wikiDirect:${result.hanzi}`
                : result.kind === `hanziWord`
                  ? result.hanziWord
                  : result.pinyinSoundId
            }
            result={result}
            onSelect={onSelectResult}
          />
        ))
      )}
    </ScrollView>
  );
}

function SearchResultItem({
  result,
  onSelect,
}: {
  result: QuickSearchResult;
  onSelect: (result: QuickSearchResult) => void;
}) {
  switch (result.kind) {
    case `hanziWord`:
      return (
        <ResultItem
          id={result}
          href={`/wiki/${encodeURIComponent(result.hanzi)}`}
          onPress={() => {
            onSelect(result);
          }}
        >
          <HanziWordResultContent hanziWord={result.hanziWord} />
        </ResultItem>
      );

    case `wikiDirect`:
      return (
        <ResultItem
          id={result}
          href={`/wiki/${encodeURIComponent(result.hanzi)}`}
          onPress={() => {
            onSelect(result);
          }}
        >
          <View className="gap-0.5">
            <View className="flex-1 flex-row items-center gap-2">
              <Text className="font-sans text-lg font-normal text-fg-loud">
                {result.hanzi}
              </Text>
              <Text className="font-sans text-sm text-fg">Open wiki page</Text>
            </View>
          </View>
        </ResultItem>
      );

    case `pinyinSound`:
      return null;

    default:
      result satisfies never;
  }
}

function HanziWordResultContent({ hanziWord }: { hanziWord: HanziWord }) {
  const db = useDb();
  const { data: dictionaryEntry } = useLiveQuery({
    query: (q) =>
      q
        .from({ entry: db.dictionaryCollection })
        .where(({ entry }) => eq(entry.hanziWord, hanziWord))
        .select(({ entry }) => ({ gloss: entry.gloss, pinyin: entry.pinyin }))
        .findOne(),
  });

  const hanzi = hanziFromHanziWord(hanziWord);
  const gloss = dictionaryEntry?.gloss[0];
  const pinyin = dictionaryEntry?.pinyin?.[0];

  return (
    <View className="gap-0.5">
      <View className="flex-1 flex-row items-center gap-2">
        <Text className="font-sans text-lg font-normal text-fg-loud">
          {hanzi}
        </Text>
        {gloss == null ? null : (
          <Text className="font-sans text-sm text-fg">{gloss}</Text>
        )}
      </View>
      {pinyin == null ? null : (
        <Text className="font-sans text-xs text-muted-fg">{pinyin}</Text>
      )}
    </View>
  );
}

function RecentQueries({
  onRemove,
  onSelect,
}: {
  onRemove: (id: string) => void;
  onSelect: () => void;
}) {
  const quickSearchHistory = useUserSettingHistory(quickSearchPickSetting);
  const items = quickSearchHistory.entries;

  if (items.length === 0) {
    return (
      <View className="items-center px-4 py-16">
        <Text className="font-sans text-lg text-muted-fg">
          No recent searches
        </Text>
      </View>
    );
  }

  return (
    <View className="">
      <Text className="px-5 pt-6 pb-4 font-sans text-base font-semibold text-fg uppercase">
        Recent
      </Text>
      {items.map((item) => {
        const objectId = item.value?.objectId;
        if (objectId == null) {
          return null;
        }

        const kind = pinyinlyObjectIdKind(objectId);

        if (kind == null) {
          return null;
        }

        switch (kind) {
          case hanziWordPinyinlyObjectIdKind: {
            const hanziWord = hanziWordFromPinyinlyObjectId(objectId);
            if (hanziWord == null) {
              return null;
            }

            return (
              <ResultItem
                key={item.id}
                id={item.id}
                onRemove={onRemove}
                onPress={onSelect}
                href={`/wiki/${encodeURIComponent(hanziFromHanziWord(hanziWord))}`}
              >
                <HanziWordResultContent hanziWord={hanziWord} />
              </ResultItem>
            );
          }

          case pinyinSoundIdPinyinlyObjectIdKind: {
            const soundId = soundIdFromPinyinlyObjectId(objectId);
            if (soundId == null) {
              return null;
            }

            return (
              <ResultItem
                key={item.id}
                id={item.id}
                onRemove={onRemove}
                onPress={onSelect}
                href={`/sounds/${encodeURIComponent(soundId)}`}
              >
                <View className="gap-0.5">
                  <View className="flex-1 flex-row items-center gap-2">
                    <Text className="font-sans text-lg font-normal text-fg-loud">
                      {soundId}
                    </Text>
                    <Text className="font-sans text-sm text-fg">Sound</Text>
                  </View>
                </View>
              </ResultItem>
            );
          }

          case skillPinyinlyObjectIdKind: {
            const skillId = skillIdFromPinyinlyObjectId(objectId);
            if (skillId == null) {
              return null;
            }

            return (
              <ResultItem
                key={item.id}
                id={item.id}
                onRemove={onRemove}
                onPress={onSelect}
                href={`/skills`}
              >
                <View className="gap-0.5">
                  <View className="flex-1 flex-row items-center gap-2">
                    <Text className="font-sans text-lg font-normal text-fg-loud">
                      {skillId}
                    </Text>
                    <Text className="font-sans text-sm text-fg">Skill</Text>
                  </View>
                </View>
              </ResultItem>
            );
          }

          case assetIdPinyinlyObjectIdKind: {
            const assetId = assetIdFromPinyinlyObjectId(objectId);
            if (assetId == null) {
              return null;
            }

            return (
              <ResultItem
                key={item.id}
                id={item.id}
                onRemove={onRemove}
                onPress={onSelect}
                href={`./`}
              >
                <View className="gap-0.5">
                  <View className="flex-1 flex-row items-center gap-2">
                    <Text className="font-sans text-lg font-normal text-fg-loud">
                      {assetId}
                    </Text>
                    <Text className="font-sans text-sm text-fg">Asset</Text>
                  </View>
                </View>
              </ResultItem>
            );
          }

          default:
            kind satisfies never;
        }

        return null;
      })}
    </View>
  );
}

function ResultItem<T>({
  children,
  href,
  id,
  onPress,
  onRemove,
}: {
  children: ViewProps[`children`];
  href: Href;
  id: T;
  onPress: () => void;
  onRemove?: (id: T) => void;
}) {
  return (
    <Link
      href={href}
      onPress={onPress}
      className={`
        flex flex-row items-center justify-between gap-3 border-t border-t-fg/10 px-5 py-4

        hover:bg-fg/5
      `}
    >
      {children}

      {onRemove == null ? null : (
        <RectButton
          variant="bare"
          iconStart="close"
          iconSize={24}
          className="text-muted-fg"
          onPress={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onRemove(id);
          }}
        />
      )}
    </Link>
  );
}

function NoResults({
  query,
  onChangeQuery,
}: {
  query: string;
  onChangeQuery: (newQuery: string) => void;
}) {
  return (
    <View className="items-center px-4 pt-10 pb-8">
      <Text className="mb-10 font-sans text-lg font-medium text-muted-fg">
        No results for &quot;<Text className={`text-fg`}>{query}</Text>&quot;
      </Text>

      <View className="w-full items-start gap-2">
        <Text className="font-sans text-sm font-semibold text-fg uppercase">
          Try searching for
        </Text>
        <View className="flex-row flex-wrap gap-2">
          {[`learn`, `学`, `good`, `coffee`].map((suggestion) => (
            <RectButton
              key={suggestion}
              onPress={() => {
                onChangeQuery(suggestion);
              }}
              variant="rounded"
            >
              {suggestion}
            </RectButton>
          ))}
        </View>
      </View>
    </View>
  );
}
